import PropTypes from 'prop-types';
import { memo, useEffect, useLayoutEffect, useState } from 'react';
import { useSelector, useDispatch } from 'store';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { Card, CardContent, Grid, LinearProgress, Button, Typography, linearProgressClasses, Skeleton } from '@mui/material';

import { getMonthlyInteractions, getGoalPeriodProviderCareStats } from 'store/slices/account';
import { humanFormattedDecimalHours } from 'utils/humanSecondsFormatter';

// the modes are sent to the server verbatim as the goal period
const RANGE_MODE_DAY = 'day';
const RANGE_MODE_WEEK = 'week';
const RANGE_MODE_MONTH = 'month';

const RANGE_MODE_OPTIONS = [
    { mode: RANGE_MODE_DAY, label: 'Daily' },
    { mode: RANGE_MODE_WEEK, label: 'Weekly' },
    { mode: RANGE_MODE_MONTH, label: 'Monthly' }
];

const PERIOD_COPY = {
    [RANGE_MODE_DAY]: 'today',
    [RANGE_MODE_WEEK]: 'this week',
    [RANGE_MODE_MONTH]: 'this month'
};

// styles
const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 30,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.light : '#fff'
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main
    }
}));

const CardStyle = styled(Card)(({ theme }) => ({
    background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
    marginBottom: '22px',
    overflow: 'hidden',
    position: 'relative'
}));

/*
2 boxes --> logged care time for the selected range (today / this week / this month),
with a progress bar against the period's summed per-day goal when one is configured
the box below is just monthly number of touches
ORG LEVEL
*/

// ==============================|| PROGRESS BAR WITH LABEL ||============================== //

function LinearProgressWithLabel({ targetHours, hoursComplete, value, testId, ...others }) {
    const theme = useTheme();

    return (
        <Grid container direction="column" spacing={1} sx={{ mt: 1.5 }} data-testid={testId}>
            <Grid item>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Typography
                            variant="caption"
                            sx={{ color: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary[800] }}
                        >
                            {/* Care session time logged <br /> */}
                            {hoursComplete} / {`${targetHours.toFixed(2)}`} hours
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography variant="caption" color="inherit">{`${Math.round(value)}%`}</Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <BorderLinearProgress variant="determinate" value={value > 100 ? 100 : value} {...others} />
            </Grid>
        </Grid>
    );
}

LinearProgressWithLabel.propTypes = {
    value: PropTypes.number.isRequired,
    hoursComplete: PropTypes.number.isRequired,
    targetHours: PropTypes.number.isRequired,
    testId: PropTypes.string
};

// ==============================|| PROGRESS BAR SKELETON ||============================== //

function CareStatsProgressSkeleton() {
    return (
        <Grid container direction="column" spacing={1} sx={{ mt: 1.5 }}>
            <Grid item>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton variant="text" width={90} />
                    </Grid>
                    <Grid item>
                        <Skeleton variant="text" width={30} />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <Skeleton variant="rounded" height={10} sx={{ borderRadius: 30 }} />
            </Grid>
        </Grid>
    );
}

// ==============================|| SIDEBAR - MENU CARD ||============================== //

const TodayCareStatsCards = () => {
    const dispatch = useDispatch();
    const theme = useTheme();

    const [rangeMode, setRangeMode] = useState(RANGE_MODE_WEEK);
    const organizationMeta = useSelector((state) => state.account.organizationMeta);
    const accountData = useSelector((state) => state.account);

    function refreshCareStatsForActiveRange() {
        // day / week / month windows and the matching goal are resolved by the server (Eastern)
        dispatch(getGoalPeriodProviderCareStats(rangeMode));
    }

    useEffect(() => {
        dispatch(getMonthlyInteractions());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationMeta]);

    // useLayoutEffect so the loading state is dispatched before the browser paints —
    // otherwise switching ranges flashes the previous range's data (still loaded) for one frame
    useLayoutEffect(() => {
        refreshCareStatsForActiveRange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rangeMode, organizationMeta]);

    useEffect(() => {
        if (accountData.customRangeProviderCareStats.needsRefresh) {
            refreshCareStatsForActiveRange();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountData.customRangeProviderCareStats.needsRefresh]);

    const careStats = accountData.customRangeProviderCareStats;
    const patientsCaredFor = accountData.monthlyInteractions.month.patientsCaredFor;
    const goalHours = careStats.custom.goalHours;
    // no goal configured for the period → hours only, no progress bar (D4)
    const hasGoalForPeriod = goalHours > 0;
    const hoursComplete = Math.round((careStats.custom.secondsLogged / 60 / 60) * 100) / 100;
    const percentageComplete = hasGoalForPeriod ? Math.round((hoursComplete / goalHours) * 100) : 0;
    const completedCopy = `You have completed ${humanFormattedDecimalHours(careStats.custom.secondsLogged)} ${PERIOD_COPY[rangeMode]}.`;

    const textSx = { opacity: theme.palette.mode === 'dark' ? 1 : 0.6, marginTop: 1 };
    const textColor = theme.palette.mode === 'dark' ? 'textSecondary' : 'grey.900';

    return (
        <>
            <CardStyle data-testid="care-stats-widget">
                <CardContent sx={{ p: 2 }}>
                    <Grid item>
                        <Typography
                            variant="h4"
                            sx={{
                                color: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary[800]
                            }}
                        >
                            My logged care
                        </Typography>
                    </Grid>
                    <Grid container justifyContent="flex-start">
                        <Grid item sx={{ marginTop: 1, marginBottom: 1 }}>
                            {RANGE_MODE_OPTIONS.map((option) => (
                                <Button
                                    key={option.mode}
                                    disableElevation
                                    variant={rangeMode === option.mode ? 'contained' : 'text'}
                                    size="small"
                                    data-testid={`care-stats-mode-${option.mode}`}
                                    sx={{ color: 'inherit', minWidth: 0, px: 1, '&.MuiButton-contained': { color: '#FFFFFF' } }}
                                    onClick={() => setRangeMode(option.mode)}
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </Grid>
                    </Grid>
                    {careStats.loaded && hasGoalForPeriod && (
                        <LinearProgressWithLabel
                            testId="care-stats-progress"
                            targetHours={goalHours}
                            hoursComplete={hoursComplete}
                            value={percentageComplete}
                        />
                    )}
                    {!careStats.loaded && <CareStatsProgressSkeleton />}
                    <Grid item>
                        {careStats.loaded ? (
                            <Typography variant="subtitle1" color={textColor} sx={textSx} data-testid="care-stats-completed">
                                {completedCopy}
                            </Typography>
                        ) : (
                            <>
                                <Skeleton variant="text" sx={{ marginTop: 1 }} />
                                <Skeleton variant="text" width="60%" />
                            </>
                        )}
                    </Grid>
                </CardContent>
            </CardStyle>
            {accountData.monthlyInteractions.loaded && (
                <CardStyle>
                    <CardContent sx={{ p: 2 }}>
                        <Grid item>
                            <Typography
                                variant="h4"
                                sx={{
                                    color: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary[800]
                                }}
                            >
                                Monthly unique <br /> patient interactions
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="subtitle1" color={textColor} sx={textSx}>
                                You have cared for {patientsCaredFor} patients so far this month.
                            </Typography>
                        </Grid>
                    </CardContent>
                </CardStyle>
            )}
        </>
    );
};

export default memo(TodayCareStatsCards);
